import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Delete } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect target must come from
// window.location.origin so the user returns to the same host they came
// from (preview / staging / production / capacitor app).
const startGoogleSignIn = () => {
  const redirect = window.location.origin + "/auth/google/callback";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
};

const getOrCreateDeviceId = () => {
  let id = localStorage.getItem("device_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("device_id", id);
  }
  return id;
};

export default function Login() {
  const [mode, setMode] = useState("loading"); // loading | pin | password | register | totp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Decide initial mode: if device has PIN + remembered email => PIN, else password
    (async () => {
      const deviceId = getOrCreateDeviceId();
      const savedEmail = localStorage.getItem("pin_email") || "";
      if (savedEmail) {
        try {
          const { data } = await axios.get(`${API}/auth/pin/exists`, {
            params: { email: savedEmail, device_id: deviceId },
          });
          if (data?.exists) {
            setRememberedEmail(savedEmail);
            setEmail(savedEmail);
            setMode("pin");
            return;
          }
        } catch {
          /* fall through to password */
        }
        // clean stale config
        localStorage.removeItem("pin_email");
      }
      setMode("password");
    })();
  }, []);

  const finishLogin = (token, user) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    // Trigger SubscriptionProvider reload so the freshly-logged user's tier
    // + isAdmin land in the context immediately (used by the welcome
    // quick-start + plan badge).
    window.dispatchEvent(new Event("subscription-updated"));
    toast.success("Bienvenido a MiNegocio");
    navigate("/");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { email, password, name }
        : { email, password, ...(totpCode ? { totp_code: totpCode } : {}) };
      const { data } = await axios.post(`${API}${endpoint}`, body);
      finishLogin(data.token, data.user);
    } catch (error) {
      const det = error.response?.data?.detail;
      // Backend signals "you also need a 2FA code" via a structured detail
      // — switch the UI to the TOTP screen without losing the entered email/pwd.
      if (det && typeof det === "object" && det.error === "totp_required") {
        setMode("totp");
        toast.message("Ingresá el código de 6 dígitos de tu app de autenticación");
      } else if (det && typeof det === "object" && det.error === "totp_invalid") {
        setTotpCode("");
        toast.error(det.message || "Código incorrecto");
      } else {
        toast.error(typeof det === "string" ? det : (det?.message || "Error en la autenticación"));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e?.preventDefault?.();
    if (totpCode.length < 6) {
      toast.error("Ingresá los 6 dígitos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, totp_code: totpCode });
      finishLogin(data.token, data.user);
    } catch (error) {
      const det = error.response?.data?.detail;
      setTotpCode("");
      toast.error(typeof det === "object" ? (det?.message || "Código inválido") : (det || "Error"));
    } finally {
      setLoading(false);
    }
  };

  const submitPin = async (fullPin) => {
    setLoading(true);
    try {
      const deviceId = getOrCreateDeviceId();
      const { data } = await axios.post(`${API}/auth/pin/verify`, {
        email: rememberedEmail,
        device_id: deviceId,
        pin: fullPin,
      });
      finishLogin(data.token, data.user);
    } catch (error) {
      setPin("");
      toast.error(error.response?.data?.detail || "Error al validar el PIN");
    } finally {
      setLoading(false);
    }
  };

  const pressPad = (digit) => {
    if (loading || pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 4) submitPin(next);
  };

  const backspace = () => {
    if (loading) return;
    setPin((p) => p.slice(0, -1));
  };

  const forgetDevice = () => {
    localStorage.removeItem("pin_email");
    setRememberedEmail("");
    setPin("");
    setEmail("");
    setMode("password");
  };

  if (mode === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9FA]">
        <div className="text-slate-500 text-sm">Cargando...</div>
      </div>
    );
  }

  if (mode === "pin") {
    const masked = rememberedEmail.replace(/^(.).+(@.+)$/, "$1•••$2");
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9FA] p-6" data-testid="pin-login-screen">
        <Card className="w-full max-w-sm p-8 bg-white border border-slate-200 rounded-sm shadow-none">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[rgb(0,47,167)] text-white mb-3">
              <KeyRound className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-[rgb(0,47,167)]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              MiNegocio
            </h1>
            <p className="text-sm text-slate-600 mt-1">Ingresá tu PIN de 4 dígitos</p>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="pin-remembered-email">{masked}</p>
          </div>

          <div className="flex justify-center gap-3 mb-6" data-testid="pin-display">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-3 w-3 rounded-full transition-colors ${
                  pin.length > i ? "bg-[rgb(0,47,167)]" : "bg-slate-200"
                }`}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <Button
                key={d}
                type="button"
                onClick={() => pressPad(d)}
                disabled={loading}
                variant="outline"
                className="h-14 text-xl font-semibold rounded-sm"
                data-testid={`pin-digit-${d}`}
              >
                {d}
              </Button>
            ))}
            <div />
            <Button
              type="button"
              onClick={() => pressPad("0")}
              disabled={loading}
              variant="outline"
              className="h-14 text-xl font-semibold rounded-sm"
              data-testid="pin-digit-0"
            >
              0
            </Button>
            <Button
              type="button"
              onClick={backspace}
              disabled={loading || pin.length === 0}
              variant="outline"
              className="h-14 rounded-sm"
              data-testid="pin-backspace"
            >
              <Delete className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-6 flex flex-col gap-1.5 text-center">
            <button
              type="button"
              onClick={() => setMode("password")}
              className="text-xs text-[rgb(0,47,167)] hover:underline"
              data-testid="pin-use-password-button"
            >
              Usar contraseña en su lugar
            </button>
            <button
              type="button"
              onClick={forgetDevice}
              className="text-xs text-slate-400 hover:text-slate-600 hover:underline"
              data-testid="pin-forget-device-button"
            >
              No es mi dispositivo
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (mode === "totp") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9FA] p-6" data-testid="totp-screen">
        <Card className="w-full max-w-sm p-8 bg-white border border-slate-200 rounded-sm shadow-none">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[rgb(0,47,167)] text-white mb-3">
              <KeyRound className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-[rgb(0,47,167)]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Verificación de 2 pasos
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Abrí tu app (Google Authenticator, Authy…) e ingresá el código de 6 dígitos.
            </p>
          </div>
          <form onSubmit={submitTotp} className="space-y-4">
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              autoFocus
              className="text-center text-2xl tracking-[0.4em] mono font-bold rounded-sm h-14"
              data-testid="totp-code-input"
            />
            <Button
              type="submit"
              disabled={loading || totpCode.length < 6}
              className="w-full btn-primary rounded-sm h-10"
              data-testid="totp-submit-button"
            >
              {loading ? "Validando…" : "Verificar y entrar"}
            </Button>
            <button
              type="button"
              onClick={() => { setMode("password"); setTotpCode(""); }}
              className="w-full text-xs text-slate-500 hover:text-slate-800"
              data-testid="totp-cancel-button"
            >
              Cancelar
            </button>
          </form>
        </Card>
      </div>
    );
  }

  // password / register
  const isRegister = mode === "register";
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9FA] p-6">
      <Card className="w-full max-w-md p-8 bg-white border border-slate-200 rounded-sm shadow-none">
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tighter mb-2 text-[rgb(0,47,167)]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            MiNegocio
          </h1>
          <p className="text-slate-500 text-sm">
            {isRegister ? "Crea una nueva cuenta" : "Inicia sesión en tu cuenta"}
          </p>
        </div>

        {rememberedEmail && !isRegister && (
          <button
            type="button"
            onClick={() => setMode("pin")}
            className="mb-4 inline-flex items-center gap-1 text-xs text-[rgb(0,47,167)] hover:underline"
            data-testid="back-to-pin-button"
          >
            <ArrowLeft className="h-3 w-3" /> Volver al PIN
          </button>
        )}

        <Button
          type="button"
          onClick={startGoogleSignIn}
          disabled={loading}
          variant="outline"
          className="w-full rounded-sm h-10 border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold"
          data-testid="google-signin-button"
        >
          <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Continuar con Google
        </Button>

        <div className="my-4 flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">o con email</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {isRegister && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Nombre</label>
              <Input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="rounded-sm"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Contraseña</label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-sm"
            />
          </div>
          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full btn-primary rounded-sm h-10"
          >
            {loading ? "Procesando..." : isRegister ? "Registrarse" : "Iniciar Sesión"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="toggle-auth-mode-button"
            type="button"
            onClick={() => setMode(isRegister ? "password" : "register")}
            className="text-sm text-[rgb(0,47,167)] hover:underline"
          >
            {isRegister ? "¿Ya tienes cuenta? Inicia sesión" : "¿No tienes cuenta? Regístrate"}
          </button>
        </div>
      </Card>
    </div>
  );
}
